/**
 * CustomPainelAparencia — o painel da paleta, que abre pelo botão do rodapé.
 *
 * ORIGEM: o esqueleto (markup, classes `select-box`/`gradient-box`, o offcanvas)
 * veio do tema AdminUX, onde o arquivo se chamava `Theming.tsx`. **O conteúdo
 * passou a ser nosso** em 28/08/2026 — as opções saem de `lib/aparencia`, os
 * rótulos estão em português e o bloco de RTL saiu.
 *
 * Por isso ele ganhou `Custom` e este aviso: enquanto era o arquivo do tema
 * intocado, o nome `Theming` era o elo com a doc do AdminUX e não se renomeava.
 * A partir do momento em que o comportamento é nosso, a regra que vale é a do
 * casco — a mesma de `CustomSidebar` e `CustomMainHeader`.
 * O mostruário original está em `theme/adminux-react/src/components/Theming.tsx`
 * (raiz do projeto, fora do git).
 *
 * ELE É O ATALHO, NÃO A TELA — e atalho aqui NÃO quer dizer versão podada. Ele
 * continua com o menu inteiro, porque é a forma rápida de escolher no meio do
 * expediente (decisão dele, 28/08). O que mudou é de onde vêm as opções: a
 * página de Aparência e este painel leem a MESMA lista, e nenhum dos dois
 * oferece o que o outro não tem. Antes eram 16 cores aqui contra 14 lá.
 *
 * O `id="theming"` do offcanvas FICA. Ele é o alvo de `data-bs-target` em dois
 * botões, e um deles vive no `AuthFooter.tsx`, que é tema puro e não se edita.
 */
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

import {
  CORES,
  ESTILOS_CABECALHO,
  ESTILOS_MENU,
  LAYOUTS_CABECALHO,
  LAYOUTS_MENU,
} from '@/lib/aparencia';
import { useTheme } from '../contexts/ThemeContext';

const CustomPainelAparencia: React.FC = () => {
  const {
    colorTheme,
    darkMode,
    headerLayout,
    headerStyle,
    sidebarLayout,
    sidebarStyle,
    setColorTheme,
    setDarkMode,
    setHeaderLayout,
    setHeaderStyle,
    setSidebarLayout,
    setSidebarStyle,
    salvar,
    desfazer,
    voltarAoPadrao,
    descartarRascunho,
    temAlteracaoNaoSalva,
  } = useTheme();

  /**
   * FECHAR O PAINEL SEM SALVAR DESFAZ — a mesma regra da tela de Aparência.
   * *"Se tem botão de salvar e resetar na tela, no painelzinho lateral também
   * tem que ter. A gente tem que ser consistente"* (28/08/2026).
   *
   * O offcanvas é do Bootstrap e não passa por React, então o gatilho é o
   * evento nativo `hidden.bs.offcanvas` — que dispara depois da animação de
   * fechar, inclusive quando se clica fora ou se aperta Esc.
   */
  useEffect(() => {
    const painel = document.getElementById('theming');
    if (!painel) return;
    painel.addEventListener('hidden.bs.offcanvas', descartarRascunho);
    return () => painel.removeEventListener('hidden.bs.offcanvas', descartarRascunho);
  }, [descartarRascunho]);

  return (
    <>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title" id="theminglabel">
          Aparência
        </h5>
        <button
          type="button"
          className="btn-close"
          data-bs-dismiss="offcanvas"
          aria-label="Fechar"
        ></button>
      </div>
      <div className="offcanvas-body casaa-painel-aparencia">
        {/* Cor do tema */}
        <div className="casaa-bloco">
          <h6 className="fw-bold">Cor do tema</h6>
          <div className="row g-2">
            {CORES.map((cor) => (
              <div key={cor.id} className="col-auto text-center">
                <div
                  className={`gradient-box ${colorTheme === cor.id ? 'active' : ''}`}
                  onClick={() => setColorTheme(cor.id)}
                  data-bs-toggle="tooltip"
                  title={cor.nome}
                >
                  {/* A amostra mostra o GRADIENTE do tema (`bg-theme-1` sob
                      `theme-{id}`), e não a cor chapada da página — é a mesma
                      escolha vista de outro jeito. */}
                  <div className={`avatar avatar-40 rounded bg-theme-1 theme-${cor.id}`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Formato do cabeçalho */}
        <div className="casaa-bloco">
          <h6 className="fw-bold">Formato do cabeçalho</h6>
          <div className="row gx-2 gy-2">
            {LAYOUTS_CABECALHO.map((layout) => (
              <div key={layout.id} className="col-auto text-center">
                <div
                  className={`select-box ${headerLayout === layout.id ? 'active' : ''}`}
                  onClick={() => setHeaderLayout(layout.id)}
                  data-bs-toggle="tooltip"
                  title={layout.nome}
                >
                  <div className="avatar avatar-40 rounded bg-theme-1-subtle text-theme-1">
                    <i className={layout.icone}></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preenchimento do cabeçalho */}
        <div className="casaa-bloco">
          <div className="row gx-2 gy-2">
            {ESTILOS_CABECALHO.map((estilo) => (
              <div key={estilo.id} className="col-auto">
                <div
                  className={`select-box ${headerStyle === estilo.id ? 'active' : ''}`}
                  onClick={() => setHeaderStyle(estilo.id)}
                  data-bs-toggle="tooltip"
                  title={estilo.nome}
                >
                  <div className={`avatar avatar-40 rounded text-theme-1 ${estilo.amostra}`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Formato do menu */}
        <div className="casaa-bloco">
          <h6 className="fw-bold">Formato do menu</h6>
          <div className="row gx-2 gy-2">
            {LAYOUTS_MENU.map((layout) => (
              <div key={layout.id} className="col-auto">
                <div
                  className={`select-box ${sidebarLayout === layout.id ? 'active' : ''}`}
                  onClick={() => setSidebarLayout(layout.id)}
                  data-bs-toggle="tooltip"
                  title={layout.nome}
                >
                  {/* Mesmo par de contraste da tela de Aparência: o accent do
                      tema é escuro demais para o modo escuro. Ver o comentário
                      lá — o defeito nasceu aqui e foi copiado para lá. */}
                  <div className="avatar avatar-40 rounded bg-theme-1-subtle text-theme-1">
                    <i className={layout.icone}></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Preenchimento do menu */}
        <div className="casaa-bloco">
          <div className="row gx-2 gy-2">
            {ESTILOS_MENU.map((estilo) => (
              <div key={estilo.id} className="col-auto">
                <div
                  className={`select-box ${sidebarStyle === estilo.id ? 'active' : ''}`}
                  onClick={() => setSidebarStyle(estilo.id)}
                  data-bs-toggle="tooltip"
                  title={estilo.nome}
                >
                  <div
                    className={`avatar avatar-40 rounded text-theme-1 ${estilo.amostra}`}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Claro / escuro */}
        <div className="casaa-bloco">
          <h6 className="fw-bold">Tema claro ou escuro</h6>
          <div className="sunmoon">
            <input
              type="checkbox"
              id="darkmodetoggle"
              checked={darkMode}
              onChange={(e) => setDarkMode(e.target.checked)}
            />
            <label htmlFor="darkmodetoggle">
              <span className="sun">☀</span>
              <span className="moon">🌙</span>
            </label>
          </div>
        </div>
        {/* AS TRÊS AÇÕES, iguais às da tela de Aparência e pela mesma razão:
            quem experimenta aqui precisa poder confirmar e voltar sem ter de
            achar a página. Ficam no fim porque é para onde se rola depois de
            escolher — e "Salvar" só acende quando há o que salvar. */}
        <div className="casaa-bloco border-top pt-3">
          <div className="d-grid gap-2">
            <button
              type="button"
              className="btn btn-theme"
              onClick={salvar}
              disabled={!temAlteracaoNaoSalva}
            >
              <i className="bi bi-check-lg me-1"></i>Salvar
            </button>
            {temAlteracaoNaoSalva && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={desfazer}
              >
                Desfazer
              </button>
            )}
            <button
              type="button"
              className="btn btn-link text-secondary"
              onClick={voltarAoPadrao}
            >
              <i className="bi bi-arrow-counterclockwise me-1"></i>Voltar ao padrão
            </button>
          </div>
        </div>

        {/* MAIS OPÇÕES — o painel e a página leem a mesma lista, mas só a
            página tem largura para mostrar o nome de cada opção embaixo da
            amostra. Aqui as amostras são só tooltip. */}
        <div className="casaa-bloco">
          <Link
            to="/minha-area/aparencia"
            className="btn btn-link ps-0"
            data-bs-dismiss="offcanvas"
          >
            Ver com os nomes, em Aparência
            <i className="bi bi-arrow-right ms-2"></i>
          </Link>
        </div>

      </div>
    </>
  );
};

export default CustomPainelAparencia;
